import { Bell, Monitor, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/utils';
import { formatDateTime } from '@/features/audit/lib/auditFormat';
import {
	isFaultNotification,
	notificationContent,
} from '../lib/notificationContent';
import type { AppNotification } from '../types';

interface NotificationItemProps {
	notification: AppNotification;
	/** Open the notification: mark it read and follow its link. */
	onOpen: (notification: AppNotification, link: string | null) => void;
	onMarkRead: (notification: AppNotification) => void;
}

export function NotificationItem({
	notification,
	onOpen,
	onMarkRead,
}: NotificationItemProps) {
	const content = notificationContent(notification);
	const unread = notification.readAt === null;
	const isFault = isFaultNotification(notification);
	const Icon = isFault
		? TriangleAlert
		: notification.category === 'display'
			? Monitor
			: Bell;

	return (
		<li className="relative">
			<button
				type="button"
				onClick={() => onOpen(notification, content.link)}
				className={cn(
					'hover:bg-accent focus-visible:bg-accent flex w-full gap-3 px-4 py-3 pr-10 text-left outline-none',
					unread && 'bg-primary/5',
				)}
			>
				<Icon
					className={cn(
						'mt-0.5 size-4 shrink-0',
						isFault ? 'text-destructive' : 'text-muted-foreground',
					)}
					aria-hidden="true"
				/>
				<span className="grid min-w-0 gap-0.5">
					<span
						className={cn(
							'text-sm break-words',
							unread ? 'font-medium' : 'text-muted-foreground',
						)}
					>
						{content.title}
					</span>
					{content.description ? (
						<span className="text-muted-foreground text-xs break-words">
							{content.description}
						</span>
					) : null}
					<span className="text-muted-foreground flex items-center gap-2 text-xs">
						<time
							dateTime={notification.createdAt}
							title={formatDateTime(notification.createdAt)}
						>
							{formatRelativeTime(notification.createdAt)}
						</time>
						{content.actionLabel ? (
							<span className="text-primary font-medium">
								{content.actionLabel} →
							</span>
						) : null}
					</span>
				</span>
				{unread ? <span className="sr-only">Unread.</span> : null}
			</button>
			{unread ? (
				<button
					type="button"
					onClick={() => onMarkRead(notification)}
					className="hover:bg-accent focus-visible:ring-ring/50 absolute top-3 right-3 grid size-5 place-items-center rounded-full outline-none focus-visible:ring-[3px]"
					aria-label={`Mark "${content.title}" as read`}
					title="Mark as read"
				>
					<span className="bg-primary size-2 rounded-full" aria-hidden="true" />
				</button>
			) : null}
		</li>
	);
}
